'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  HelpCircle,
  X,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type AlertType = 'success' | 'error' | 'warning' | 'info' | 'confirm';

export interface AlertOptions {
  id?: string;
  type?: AlertType;
  title?: React.ReactNode;
  message?: React.ReactNode;
  description?: React.ReactNode; // alias for message
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
  autoClose?: number | false; // duration in ms, or false
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  onClose?: () => void;
  icon?: React.ReactNode;
  customContent?: React.ReactNode;
}

interface AlertState extends AlertOptions {
  isOpen: boolean;
  isLoading?: boolean;
}

// Global alert listener for programmatic non-hook usage
type AlertListener = (alert: AlertState | null) => void;
let globalAlertListener: AlertListener | null = null;
let pendingAlertState: AlertState | null = null;

export const showAlert = (options: AlertOptions | string) => {
  const opts: AlertOptions =
    typeof options === 'string' ? { message: options, type: 'info' } : options;

  const alertState: AlertState = {
    ...opts,
    id: opts.id || Math.random().toString(36).substring(2, 9),
    isOpen: true,
    type: opts.type || 'info',
  };

  if (globalAlertListener) {
    globalAlertListener(alertState);
  } else {
    pendingAlertState = alertState;
  }
};

showAlert.success = (messageOrOpts: string | Omit<AlertOptions, 'type'>, title?: string) => {
  if (typeof messageOrOpts === 'string') {
    showAlert({ type: 'success', title: title || 'Success', message: messageOrOpts });
  } else {
    showAlert({ ...messageOrOpts, type: 'success', title: messageOrOpts.title || 'Success' });
  }
};

showAlert.error = (messageOrOpts: string | Omit<AlertOptions, 'type'>, title?: string) => {
  if (typeof messageOrOpts === 'string') {
    showAlert({ type: 'error', title: title || 'Error Occurred', message: messageOrOpts });
  } else {
    showAlert({ ...messageOrOpts, type: 'error', title: messageOrOpts.title || 'Error Occurred' });
  }
};

showAlert.warning = (messageOrOpts: string | Omit<AlertOptions, 'type'>, title?: string) => {
  if (typeof messageOrOpts === 'string') {
    showAlert({ type: 'warning', title: title || 'Attention Required', message: messageOrOpts });
  } else {
    showAlert({ ...messageOrOpts, type: 'warning', title: messageOrOpts.title || 'Attention Required' });
  }
};

showAlert.info = (messageOrOpts: string | Omit<AlertOptions, 'type'>, title?: string) => {
  if (typeof messageOrOpts === 'string') {
    showAlert({ type: 'info', title: title || 'Information', message: messageOrOpts });
  } else {
    showAlert({ ...messageOrOpts, type: 'info', title: messageOrOpts.title || 'Information' });
  }
};

showAlert.confirm = (options: Omit<AlertOptions, 'type'>) => {
  showAlert({
    ...options,
    type: 'confirm',
    title: options.title || 'Are you sure?',
    showCancel: true,
    confirmText: options.confirmText || 'Confirm',
    cancelText: options.cancelText || 'Cancel',
  });
};

showAlert.close = () => {
  if (globalAlertListener) {
    globalAlertListener(null);
  }
};

// Context for React tree control if desired
interface AlertContextType {
  showAlert: typeof showAlert;
  closeAlert: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = () => {
  const context = useContext(AlertContext);
  return {
    show: showAlert,
    success: showAlert.success,
    error: showAlert.error,
    warning: showAlert.warning,
    info: showAlert.info,
    confirm: showAlert.confirm,
    close: showAlert.close,
    ...context,
  };
};

const ALERT_CONFIG: Record<
  AlertType,
  {
    icon: React.ElementType;
    iconBg: string;
    iconRing: string;
    iconColor: string;
    glowBg: string;
    primaryBtnClass: string;
    defaultTitle: string;
    defaultBtnText: string;
  }
> = {
  success: {
    icon: CheckCircle2,
    iconBg: 'bg-emerald-500/15 dark:bg-emerald-500/20',
    iconRing: 'border-emerald-500/30 ring-emerald-500/10',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    glowBg: 'from-emerald-500/10 to-transparent',
    primaryBtnClass:
      'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20 border-emerald-600/30',
    defaultTitle: 'Success!',
    defaultBtnText: 'Continue',
  },
  error: {
    icon: XCircle,
    iconBg: 'bg-rose-500/15 dark:bg-rose-500/20',
    iconRing: 'border-rose-500/30 ring-rose-500/10',
    iconColor: 'text-rose-600 dark:text-rose-400',
    glowBg: 'from-rose-500/10 to-transparent',
    primaryBtnClass:
      'bg-rose-600 hover:bg-rose-700 text-white shadow-lg shadow-rose-600/20 border-rose-600/30',
    defaultTitle: 'Something went wrong',
    defaultBtnText: 'Dismiss',
  },
  warning: {
    icon: AlertTriangle,
    iconBg: 'bg-amber-500/15 dark:bg-amber-500/20',
    iconRing: 'border-amber-500/30 ring-amber-500/10',
    iconColor: 'text-amber-600 dark:text-amber-400',
    glowBg: 'from-amber-500/10 to-transparent',
    primaryBtnClass:
      'bg-amber-600 hover:bg-amber-700 text-white shadow-lg shadow-amber-600/20 border-amber-600/30',
    defaultTitle: 'Warning',
    defaultBtnText: 'Understand',
  },
  info: {
    icon: Info,
    iconBg: 'bg-blue-500/15 dark:bg-blue-500/20',
    iconRing: 'border-blue-500/30 ring-blue-500/10',
    iconColor: 'text-blue-600 dark:text-blue-400',
    glowBg: 'from-blue-500/10 to-transparent',
    primaryBtnClass:
      'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20 border-blue-600/30',
    defaultTitle: 'Notice',
    defaultBtnText: 'Got It',
  },
  confirm: {
    icon: HelpCircle,
    iconBg: 'bg-primary/15 dark:bg-primary/20',
    iconRing: 'border-primary/30 ring-primary/10',
    iconColor: 'text-primary dark:text-primary',
    glowBg: 'from-primary/10 to-transparent',
    primaryBtnClass:
      'bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20',
    defaultTitle: 'Confirmation',
    defaultBtnText: 'Confirm',
  },
};

export interface AlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  type?: AlertType;
  title?: React.ReactNode;
  message?: React.ReactNode;
  description?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
  autoClose?: number | false;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  icon?: React.ReactNode;
  customContent?: React.ReactNode;
  isLoading?: boolean;
}

export function AlertModal({
  isOpen,
  onClose,
  type = 'info',
  title,
  message,
  description,
  confirmText,
  cancelText = 'Cancel',
  showCancel = false,
  autoClose = false,
  onConfirm,
  onCancel,
  icon,
  customContent,
  isLoading = false,
}: AlertModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const config = ALERT_CONFIG[type] || ALERT_CONFIG.info;
  const IconComponent = config.icon;

  const contentMessage = message || description;
  const displayTitle = title ?? config.defaultTitle;
  const displayConfirmText = confirmText ?? config.defaultBtnText;

  // Auto-close handling
  useEffect(() => {
    if (isOpen && autoClose && typeof autoClose === 'number' && autoClose > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoClose);
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, onClose]);

  const handleConfirm = async () => {
    if (onConfirm) {
      try {
        setIsSubmitting(true);
        await onConfirm();
        onClose();
      } catch (err) {
        console.error('Error during alert confirmation:', err);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      onClose();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onClose();
  };

  // Keyboard navigation (Escape key)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleCancel();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 select-none">
          {/* Backdrop overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleCancel}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-all"
          />

          {/* Modal box: 350px x 350px with all content perfectly centered */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 10 }}
            transition={{
              type: 'spring',
              stiffness: 400,
              damping: 30,
            }}
            className="relative w-[350px] h-[350px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] overflow-hidden rounded-2xl border border-border/80 bg-card/95 backdrop-blur-md p-6 shadow-2xl shadow-black/20 dark:border-border/40 dark:bg-card/90 flex flex-col items-center justify-center text-center"
            role="alertdialog"
            aria-modal="true"
          >
            {/* Top ambient glow */}
            <div
              className={cn(
                'absolute -top-20 left-1/2 -translate-x-1/2 h-32 w-48 rounded-full bg-gradient-to-b opacity-40 blur-2xl pointer-events-none',
                config.glowBg
              )}
            />

            {/* Close button in top right */}
            <button
              onClick={handleCancel}
              className="absolute right-3.5 top-3.5 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring z-10"
              aria-label="Close alert"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Centered Content Container */}
            <div className="flex flex-col items-center justify-center text-center w-full h-full my-auto px-1">
              {/* Icon Header */}
              <motion.div
                initial={{ scale: 0.5, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 450,
                  damping: 20,
                  delay: 0.05,
                }}
                className={cn(
                  'relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border-2 ring-6 ring-offset-0 transition-all duration-300',
                  config.iconBg,
                  config.iconRing
                )}
              >
                {icon ? (
                  icon
                ) : (
                  <IconComponent
                    className={cn('h-7 w-7 stroke-[2.2]', config.iconColor)}
                  />
                )}
              </motion.div>

              {/* Title */}
              <motion.h3
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="mt-3.5 text-lg font-bold tracking-tight text-foreground font-heading line-clamp-1"
              >
                {displayTitle}
              </motion.h3>

              {/* Message / Description */}
              {contentMessage && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="mt-2 text-sm text-muted-foreground leading-relaxed px-1 break-words font-normal max-h-[72px] overflow-y-auto"
                >
                  {typeof contentMessage === 'string' ? (
                    <p>{contentMessage}</p>
                  ) : (
                    contentMessage
                  )}
                </motion.div>
              )}

              {/* Custom Child Content */}
              {customContent && <div className="mt-2 w-full">{customContent}</div>}

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-5 flex w-full flex-col-reverse sm:flex-row items-center gap-2 justify-center"
              >
                {(showCancel || type === 'confirm') && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    disabled={isSubmitting || isLoading}
                    className="w-full sm:w-auto min-w-[90px] h-9 rounded-xl border-border bg-background/50 hover:bg-muted text-foreground transition-all duration-200 text-xs font-medium"
                  >
                    {cancelText}
                  </Button>
                )}

                <Button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isSubmitting || isLoading}
                  className={cn(
                    'w-full sm:w-auto min-w-[110px] h-9 rounded-xl font-semibold text-xs transition-all duration-200 active:scale-95 flex items-center justify-center gap-2',
                    config.primaryBtnClass
                  )}
                >
                  {isSubmitting || isLoading ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    displayConfirmText
                  )}
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

/**
 * Global Alert Container to place in RootLayout.
 * Listens to `showAlert.success(...)`, `showAlert.error(...)`, `showAlert.warning(...)`, etc.
 */
export function AlertContainer() {
  const [alert, setAlert] = useState<AlertState | null>(null);

  useEffect(() => {
    globalAlertListener = (newAlert) => {
      setAlert(newAlert);
    };
    if (pendingAlertState) {
      setAlert(pendingAlertState);
      pendingAlertState = null;
    }
    return () => {
      globalAlertListener = null;
    };
  }, []);

  if (!alert) return null;

  const handleClose = () => {
    if (alert.onClose) {
      alert.onClose();
    }
    setAlert(null);
  };

  return (
    <AlertModal
      isOpen={alert.isOpen}
      onClose={handleClose}
      type={alert.type}
      title={alert.title}
      message={alert.message}
      description={alert.description}
      confirmText={alert.confirmText}
      cancelText={alert.cancelText}
      showCancel={alert.showCancel}
      autoClose={alert.autoClose}
      onConfirm={alert.onConfirm}
      onCancel={alert.onCancel}
      icon={alert.icon}
      customContent={alert.customContent}
      isLoading={alert.isLoading}
    />
  );
}

export function AlertProvider({ children }: { children: React.ReactNode }) {
  return (
    <AlertContext.Provider value={{ showAlert, closeAlert: showAlert.close }}>
      {children}
      <AlertContainer />
    </AlertContext.Provider>
  );
}
